import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import type { AppClassSpec, DeploymentTarget } from '@infras/shared-types';
import { CopyButton } from '../components/CopyButton';
import { DetailHead } from '../components/DetailHead';
import { useCrumb } from '../lib/crumb';
import { RefreshButton } from '../components/RefreshButton';

/**
 * Deployment catalog — "HOW we deploy a class of application" (vs the
 * inventories = what exists). One component, two views: /catalog (list) and
 * /catalog/:name (the full recipe, parsed from specJson as AppClassSpec).
 * Rows come from the versioned seed (inventory-core catalog/), synced at api boot.
 */
const LIST = gql`
  query CatalogEntities {
    catalogEntities {
      kind
      name
      title
      lifecycle
      tags
      updatedAt
    }
  }
`;

const DETAIL = gql`
  query CatalogEntity($name: String!) {
    catalogEntity(name: $name) {
      kind
      name
      title
      description
      lifecycle
      tags
      annotationsJson
      specJson
      updatedAt
    }
  }
`;

interface EntityRow {
  kind: string;
  name: string;
  title?: string;
  description?: string;
  lifecycle: string;
  tags: string[];
  updatedAt: string;
}
interface EntityDetail extends EntityRow {
  annotationsJson: string;
  specJson: string;
}

const DETAIL_SECTIONS: { id: string; label: string }[] = [
  { id: 'matrix', label: 'Matrix' },
  { id: 'layers', label: 'Layers' },
  { id: 'build', label: 'Build' },
  { id: 'secrets', label: 'Secrets' },
  { id: 'env-vars', label: 'Env vars' },
  { id: 'k8s', label: 'K8s' },
  { id: 'runbook', label: 'Runbook' },
  { id: 'issues', label: 'Issues' },
  { id: 'deps', label: 'Deps' },
  { id: 'lineage', label: 'Lineage' },
];

/** Split seed prose on blank lines into readable paragraphs. */
function ProseBlocks({ text, className = 'catalog-prose' }: { text: string; className?: string }) {
  const blocks = text
    .split(/\n\n+/)
    .map((b) => b.trim())
    .filter(Boolean);
  if (blocks.length <= 1) {
    return <p className={className}>{text}</p>;
  }
  return (
    <div className={className}>
      {blocks.map((b) => (
        <p key={b.slice(0, 48)}>{b}</p>
      ))}
    </div>
  );
}

/** Env vars (need _), package ids, hostnames — mono chips; prose stays muted. */
const TECH_TOKEN =
  /(@[\w.-]+\/[\w.-]+|[A-Z][A-Z0-9]*_[A-Z0-9_]+|https?:\/\/[^\s)]+|[\w.-]+\.(?:opstech|optisigns)\.org(?:\/[\w./-]*)?)/g;

function TechText({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(TECH_TOKEN)) {
    const i = m.index ?? 0;
    if (i > last) parts.push(text.slice(last, i));
    parts.push(
      <code key={`${i}-${m[0]}`} className="catalog-tech">
        {m[0]}
      </code>,
    );
    last = i + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

/** Seed lines like `pkg (role note)` → chip + muted note. */
function splitLabeled(item: string): { primary: string; note?: string } {
  const m = item.match(/^(.+?)\s+\((.+)\)\s*$/);
  if (m) return { primary: m[1].trim(), note: m[2].trim() };
  return { primary: item.trim() };
}

function DepList({ items, kind }: { items: string[]; kind: 'packages' | 'prose' }) {
  return (
    <ul className={kind === 'packages' ? 'catalog-dep-chips' : 'catalog-dep-list'}>
      {items.map((raw) => {
        const { primary, note } = splitLabeled(raw);
        if (kind === 'packages') {
          return (
            <li key={raw} className="catalog-dep-chip-item">
              <code className="catalog-tech catalog-tech--pkg">{primary}</code>
              {note ? <span className="catalog-dep-note muted">{note}</span> : null}
            </li>
          );
        }
        return (
          <li key={raw}>
            <TechText text={raw} />
          </li>
        );
      })}
    </ul>
  );
}

/** List-row tags — show a few quiet chips; overflow as +N (full set on detail). */
function TagChips({ tags, limit = 3 }: { tags: string[]; limit?: number }) {
  if (!tags.length) return null;
  const shown = tags.slice(0, limit);
  const rest = tags.length - shown.length;
  return (
    <span className="tag-list catalog-list-tags">
      {shown.map((t) => (
        <code key={t} className="tag-badge tag-badge--quiet">
          {t}
        </code>
      ))}
      {rest > 0 ? (
        <span className="catalog-tag-more muted" title={tags.slice(limit).join(', ')}>
          +{rest}
        </span>
      ) : null}
    </span>
  );
}

export function CatalogPage() {
  const { name } = useParams();
  // Breadcrumb tail on the entity view; the list view has none. Above any early
  // return, or it becomes a conditional hook.
  useCrumb(name ?? null);
  return name ? <CatalogDetail name={name} /> : <CatalogList />;
}

function CatalogList() {
  const { data, loading, error, refetch } = useQuery<{ catalogEntities: EntityRow[] }>(LIST, {
    fetchPolicy: 'cache-and-network',
  });
  const rows = data?.catalogEntities ?? [];
  return (
    <div className="page">
      <div className="detail-head">
        <h2>Deploy catalog</h2>
        <span className="badge quiet">
          {rows.length} entr{rows.length === 1 ? 'y' : 'ies'}
        </span>
        <RefreshButton refetch={() => refetch().catch(() => undefined)} />
      </div>
      <p className="detail-desc">
        Inventories record what exists; the catalog is the deploy recipe (build → CI →
        placement → secrets). Agents read the same rows via <code>list_catalog</code> /{' '}
        <code>get_catalog</code>.
      </p>
      {error && <p className="error">{error.message}</p>}
      {loading && !rows.length ? (
        <p className="muted">Loading…</p>
      ) : (
        <table className="catalog-list">
          <thead>
            <tr>
              <th>Name</th>
              <th>Kind</th>
              <th>Lifecycle</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.kind}:${r.name}`}>
                <td className="catalog-list-name">
                  <Link className="table-title" to={`/catalog/${r.name}`}>
                    {r.title ?? r.name}
                  </Link>
                  <TagChips tags={r.tags} limit={3} />
                </td>
                <td className="muted catalog-list-meta">{r.kind}</td>
                <td className="catalog-list-meta">
                  <span className={`badge ${r.lifecycle === 'deprecated' ? 'inferred' : 'confirmed'}`}>
                    {r.lifecycle}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CatalogDetail({ name }: { name: string }) {
  // Tabbed sections — one at a time instead of a 10-section scroll. Hash is
  // kept in sync so #build deep-links still land on the right tab. Declared
  // before any early return (Rules of Hooks).
  const [active, setActive] = useState<string>(() => {
    const h = window.location.hash.slice(1);
    return DETAIL_SECTIONS.some((sec) => sec.id === h) ? h : DETAIL_SECTIONS[0].id;
  });
  const openSection = (id: string) => {
    setActive(id);
    window.history.replaceState(null, '', `#${id}`);
  };
  const { data, loading, error, refetch } = useQuery<{ catalogEntity: EntityDetail | null }>(DETAIL, {
    variables: { name },
    fetchPolicy: 'cache-and-network',
  });
  const e = data?.catalogEntity;
  if (loading && !e) return <div className="page"><p className="muted">Loading…</p></div>;
  if (error) return <div className="page"><p className="error">{error.message}</p></div>;
  if (!e) {
    return (
      <div className="page">
        <p className="muted">
          No catalog entry named <strong>{name}</strong>. <Link to="/catalog">Back to catalog</Link>
        </p>
      </div>
    );
  }
  const spec = JSON.parse(e.specJson) as AppClassSpec;
  const annotations = JSON.parse(e.annotationsJson) as Record<string, string>;
  const isDeprecated = e.lifecycle === 'deprecated';

  return (
    <div className="page catalog-detail">
      <div className="detail-back">
        <Link to="/catalog" className="muted">← Deploy catalog</Link>
      </div>
      <DetailHead
        name={e.name}
        badge={
          <span className={`badge ${isDeprecated ? 'inferred' : 'confirmed'}`}>{e.lifecycle}</span>
        }
        facts={[e.title !== e.name && e.title, e.kind, ...e.tags]}
        /* The catalog is a hand-maintained seed, synced from catalog/seed.ts on
           every api boot — claiming a scan time it doesn't have would be a lie. */
        curated
        scanNote="Synced from the repo seed on each deploy."
        /* Owner decision e-22: ONE refresh for this page, not one per section band.
           Every section below — matrix, layers, secrets, env-vars — is drawn from
           the SAME `catalogEntity` query, so four controls were four ways to fire
           one refetch, and beside a heading each read as if it refreshed only that
           table. One control, at the top, where the page's identity is. */
        actions={
          <>
            <CopyButton value={e.name} />
            <RefreshButton refetch={() => refetch().catch(() => undefined)} />
          </>
        }
      />

      {isDeprecated && (
        <div className="catalog-callout catalog-callout-warn" role="note">
          <strong>Deprecated.</strong> Operate or migrate existing apps only — do not bootstrap new
          ones on this recipe.
          {annotations['infras/successor'] && (
            <>
              {' '}
              Successor:{' '}
              <Link to={`/catalog/${annotations['infras/successor']}`}>
                {annotations['infras/successor']}
              </Link>
              .
            </>
          )}
        </div>
      )}

      <ProseBlocks text={e.description ?? ''} className="detail-desc catalog-lede" />

      <ul className="catalog-meta" aria-label="Catalog annotations">
        {Object.entries(annotations).map(([k, v]) => (
          <li key={k} className="catalog-meta-item">
            <span className="catalog-meta-key">{k}</span>
            {v.startsWith('http') ? (
              <a href={v} target="_blank" rel="noreferrer" className="catalog-meta-val">
                {v}
              </a>
            ) : k === 'infras/successor' ? (
              <Link to={`/catalog/${v}`} className="catalog-meta-val">
                {v}
              </Link>
            ) : (
              <code className="catalog-meta-val">{v}</code>
            )}
          </li>
        ))}
      </ul>

      <nav className="catalog-toc catalog-tabs" aria-label="Sections">
        {DETAIL_SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={s.id === active ? 'active' : undefined}
            aria-current={s.id === active ? 'true' : undefined}
            onClick={() => openSection(s.id)}
          >
            {s.label}
          </button>
        ))}
      </nav>

      {active === 'matrix' && (
        <section className="catalog-section">
        <h2 id="matrix">Deployment matrix</h2>
        <p className="detail-desc">
          One column per env — locate and re-verify a deployment.{' '}
          <code>&lt;app&gt;</code> = app slug. Verified 2026-07-13.
        </p>
        <DeploymentMatrix envs={spec.environments} />
        <div className="catalog-note">
          <h3>Deploy strategy</h3>
          <ProseBlocks text={spec.ci.deployStrategy} />
          <p className="muted text-sm" style={{ marginTop: 8 }}>
            Kubeconfig: <code className="catalog-tech">{spec.ci.kubeconfig.gcpSecret}</code>
            {' — '}
            {spec.ci.kubeconfig.note}
          </p>
          {spec.ci.notes.length > 0 && (
            <ul className="catalog-bullets">
              {spec.ci.notes.map((n) => (
                <li key={n}>
                  <TechText text={n} />
                </li>
              ))}
            </ul>
          )}
        </div>
        </section>
      )}

      {active === 'layers' && (
        <section className="catalog-section">
        <h2 id="layers">Layers</h2>
        <div className="catalog-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Layer</th>
                <th>Ships as</th>
                <th>Port</th>
                <th>Dockerfile</th>
                <th>Status</th>
                <th>Role</th>
                {spec.layers.some((l) => l.repo) && <th>Repo</th>}
              </tr>
            </thead>
            <tbody>
              {spec.layers.map((l) => (
                <tr key={l.key}>
                  <td>
                    <strong>{l.key}</strong>
                  </td>
                  <td>
                    <code className="catalog-tech">{l.shipsAs}</code>
                  </td>
                  <td className="muted">{l.port ?? '—'}</td>
                  <td className="muted">
                    {l.dockerfile ? <code className="catalog-tech">{l.dockerfile}</code> : '—'}
                  </td>
                  <td>
                    {l.status === 'active' ? (
                      <span className="mtx-yes">active</span>
                    ) : (
                      <span className="muted">todo</span>
                    )}
                  </td>
                  <td className="muted text-sm catalog-cell-prose">
                    <TechText text={l.role} />
                  </td>
                  {spec.layers.some((x) => x.repo) && (
                    <td className="muted text-xs catalog-cell-prose">
                      {l.repo ? <TechText text={l.repo} /> : '—'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </section>
      )}

      {active === 'build' && (
        <section className="catalog-section">
        <h2 id="build">Build</h2>
        <p className="detail-desc">
          Base image <code className="catalog-tech">{spec.build.baseImage}</code> · context{' '}
          <code className="catalog-tech">{spec.build.context}</code> · registry{' '}
          <code className="catalog-tech">{spec.build.registry}</code>
        </p>
        <p className="detail-desc catalog-chip-row">
          Tags:{' '}
          {spec.build.tags.map((t) => (
            <code key={t} className="tag-badge">
              {t}
            </code>
          ))}
        </p>
        {spec.build.notes && spec.build.notes.length > 0 && (
          <ul className="catalog-bullets">
            {spec.build.notes.map((n) => (
              <li key={n}>
                <TechText text={n} />
              </li>
            ))}
          </ul>
        )}
        </section>
      )}

      {active === 'secrets' && (
        <section className="catalog-section">
        <h2 id="secrets">Secrets &amp; config</h2>
        <div className="catalog-note">
          <h3>Bundle convention</h3>
          <ProseBlocks text={spec.secrets.bundleConvention} />
          <p className="detail-desc" style={{ marginTop: 12 }}>
            envFrom: Secret <code className="catalog-tech">{spec.secrets.envFromSecret}</code> +
            ConfigMap <code className="catalog-tech">{spec.secrets.envFromConfigMap}</code>
          </p>
        </div>
        <div className="catalog-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Script</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              {spec.secrets.scripts.map((s) => (
                <tr key={s.name}>
                  <td>
                    <code className="catalog-tech">{s.name}</code>
                  </td>
                  <td className="muted text-sm catalog-cell-prose">
                    <TechText text={s.purpose} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </section>
      )}

      {active === 'env-vars' && (
        <section className="catalog-section">
        <h2 id="env-vars">Env-var contract</h2>
        <p className="detail-desc">Names only — never values.</p>
        <div className="catalog-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Plane</th>
                <th>Variables</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(spec.envVars).map(([plane, vars]) => (
                <tr key={plane}>
                  <td>
                    <strong>{plane}</strong>
                  </td>
                  <td className="catalog-chip-row">
                    {vars.map((v) => (
                      <code key={v} className="catalog-tech">
                        {v}
                      </code>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </section>
      )}

      {active === 'k8s' && (
        <section className="catalog-section">
        <h2 id="k8s">K8s placement</h2>
        <dl className="catalog-kv">
          <div>
            <dt>Deployment</dt>
            <dd>
              <code className="catalog-tech">{spec.k8s.deploymentName}</code>
              <span className="muted"> · replicas {spec.k8s.replicas}</span>
            </dd>
          </div>
          <div>
            <dt>Probes</dt>
            <dd className="muted">
              <TechText text={spec.k8s.probes} />
            </dd>
          </div>
          <div>
            <dt>Resources</dt>
            <dd className="muted">
              <TechText text={spec.k8s.resources} />
            </dd>
          </div>
          <div>
            <dt>Ingress</dt>
            <dd>
              <TechText text={spec.k8s.ingress.shared} />
              <span className="muted"> · path </span>
              <code className="catalog-tech">{spec.k8s.ingress.pathPattern}</code>
            </dd>
          </div>
        </dl>
        </section>
      )}

      {active === 'runbook' && (
        <section className="catalog-section">
        <h2 id="runbook">Deploy runbook</h2>
        <ol className="catalog-runbook">
          {spec.runbook.map((step, i) => (
            <li key={i}>
              <TechText text={step} />
            </li>
          ))}
        </ol>
        </section>
      )}

      {active === 'issues' && (
        <section className="catalog-section">
        <h2 id="issues">Known issues</h2>
        <ul className="catalog-issues">
          {spec.knownIssues.map((k) => {
            // Matches SEED DATA, not UI: some catalog key names literally begin
            // with a warning sign. This is the one deliberate exception to the
            // no-emoji rule — it's a pattern over content, not a glyph we render.
            const warn = /^DEPRECATED/i.test(k) || /^\u26A0/.test(k);
            return (
              <li key={k} className={warn ? 'catalog-issue catalog-issue-warn' : 'catalog-issue'}>
                <TechText text={k} />
              </li>
            );
          })}
        </ul>
        </section>
      )}

      {active === 'deps' && (
        <section className="catalog-section">
        <h2 id="deps">Dependencies</h2>
        <div className="catalog-deps">
          <section className="catalog-dep-group" aria-labelledby="deps-packages">
            <h3 id="deps-packages">Packages</h3>
            <DepList items={spec.dependencies.packages} kind="packages" />
          </section>
          <section className="catalog-dep-group" aria-labelledby="deps-datastores">
            <h3 id="deps-datastores">Datastores</h3>
            <DepList items={spec.dependencies.datastores} kind="prose" />
          </section>
          <section className="catalog-dep-group" aria-labelledby="deps-external">
            <h3 id="deps-external">External</h3>
            <DepList items={spec.dependencies.external} kind="prose" />
          </section>
        </div>
        </section>
      )}

      {active === 'lineage' && (
        <section className="catalog-section">
        <h2 id="lineage">Lineage</h2>
        <ul className="catalog-lineage">
          {spec.lineage.map((l) => (
            <li key={l.version} className="catalog-lineage-item">
              <div className="catalog-lineage-head">
                <strong className="catalog-lineage-ver">{l.version}</strong>
                {l.status === 'current' ? (
                  <span className="mtx-yes">current</span>
                ) : (
                  <span className="muted text-xs">legacy</span>
                )}
              </div>
              <p className="catalog-lineage-marker muted">
                <TechText text={l.marker} />
              </p>
            </li>
          ))}
        </ul>
        </section>
      )}

    </div>
  );
}

/**
 * The deployment matrix — transposed (attributes down, environments across) so
 * each env reads as one column. `null` renders as "verify" (never a fake value).
 */
function DeploymentMatrix({ envs }: { envs: DeploymentTarget[] }) {
  // Notes are long operational prose — collapsed by default so the matrix
  // stays scannable; one click expands the row.
  const [notesOpen, setNotesOpen] = useState(false);
  const code = (v: string | null) =>
    v == null ? (
      <span className="muted">verify</span>
    ) : (
      <code className="catalog-tech catalog-tech--cell">{v}</code>
    );
  const gate = (t: DeploymentTarget) =>
    t.gate === 'manual' ? (
      <span className="mtx-yes">manual</span>
    ) : (
      <span className="muted">{t.gate}</span>
    );

  const ROWS: { label: string; cell: (t: DeploymentTarget) => ReactNode }[] = [
    { label: 'Deployed by', cell: (t) => <span className="text-sm catalog-cell-prose">{t.deployedBy}</span> },
    { label: 'CI branch', cell: (t) => code(t.branch) },
    { label: 'Gate', cell: gate },
    { label: 'Cluster', cell: (t) => code(t.cluster) },
    { label: 'Cluster ID', cell: (t) => code(t.clusterId) },
    { label: 'Namespace', cell: (t) => code(t.namespace) },
    { label: 'Kubeconfig', cell: (t) => code(t.kubeconfigSecret) },
    { label: 'K8s Secret', cell: (t) => code(t.k8sSecret) },
    { label: 'Backend URL', cell: (t) => code(t.backendUrl) },
    { label: 'Ingress LB IP', cell: (t) => code(t.ingressIp) },
    { label: 'Ingress', cell: (t) => code(t.ingressName) },
    { label: 'TLS secret', cell: (t) => code(t.tlsSecret) },
    { label: 'CDN URL', cell: (t) => code(t.cdnUrl) },
    { label: 'S3 bucket', cell: (t) => code(t.s3Bucket) },
    { label: 'CloudFront ID', cell: (t) => code(t.cloudfrontId) },
    { label: 'AWS account (CDN)', cell: (t) => code(t.awsAccount) },
    { label: 'CDN base path', cell: (t) => code(t.cdnBasePath) },
    { label: 'GCP secrets project', cell: (t) => code(t.gcpSecretsProject) },
    { label: 'Keys bundle', cell: (t) => code(t.keysBundle) },
  ];

  return (
    <div className="catalog-table-wrap catalog-matrix">
      <table>
        <thead>
          <tr>
            <th className="catalog-matrix-attr">Attribute</th>
            {envs.map((t) => (
              <th key={t.env} style={{ textTransform: 'capitalize' }}>
                {t.env}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((r) => (
            <tr key={r.label}>
              <th className="catalog-matrix-attr" scope="row">
                {r.label}
              </th>
              {envs.map((t) => (
                <td key={t.env}>{r.cell(t)}</td>
              ))}
            </tr>
          ))}
          {envs.some((t) => t.notes) && (
            <tr>
              <th className="catalog-matrix-attr" scope="row">
                Notes
              </th>
              {notesOpen ? (
                envs.map((t) => (
                  <td key={t.env}>
                    {t.notes ? (
                      <span className="muted text-sm catalog-cell-prose">
                        <TechText text={t.notes} />
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                ))
              ) : (
                <td colSpan={envs.length}>
                  <button className="mini-btn" onClick={() => setNotesOpen(true)}>
                    Show notes
                  </button>
                </td>
              )}
            </tr>
          )}
          {envs.some((t) => t.notes) && notesOpen && (
            <tr>
              <th className="catalog-matrix-attr" scope="row" />
              <td colSpan={envs.length}>
                <button className="mini-btn" onClick={() => setNotesOpen(false)}>
                  Hide notes
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
